/**
 * Who a notify action emails from the row itself: whoever made the change,
 * whoever created the row, and the people a people column holds. Named people
 * are picked beside it with `AutomationPeoplePicker`.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Checkbox, Popover, PopoverContent, PopoverTrigger } from '@monksflow/monks-ui';

import type { AutomationUserRef } from '@/entities/tracker-automation';

import type { CustomColumn } from '../../lib/useTrackersBoard';

interface AutomationRowRecipientsPickerProps {
  refs: AutomationUserRef[];
  columnIds: string[];
  /** The board's people columns. */
  peopleColumns: CustomColumn[];
  /** A schedule or a date fires the rule, so nobody makes the change. */
  offerActor: boolean;
  /** Record rows are the platform's, and have no creator to email. */
  offerCreator: boolean;
  onChange: (next: { recipientRefs: AutomationUserRef[]; recipientColumnIds: string[] }) => void;
}

export function AutomationRowRecipientsPicker({
  refs,
  columnIds,
  peopleColumns,
  offerActor,
  offerCreator,
  onChange,
}: AutomationRowRecipientsPickerProps) {
  const { t } = useTranslation('trackers');
  const [open, setOpen] = useState(false);

  const refOptions: Array<{ ref: AutomationUserRef; label: string }> = [
    ...(offerActor
      ? [
          {
            // eslint-disable-next-line i18next/no-literal-string -- user ref id
            ref: 'actor' as const,
            label: t('trackersBoard.automationRuleFields.whoeverMadeTheChange'),
          },
        ]
      : []),
    ...(offerCreator
      ? [
          {
            // eslint-disable-next-line i18next/no-literal-string -- user ref id
            ref: 'creator' as const,
            label: t('trackersBoard.automationRuleFields.whoeverCreatedTheRow'),
          },
        ]
      : []),
  ];
  if (refOptions.length === 0 && peopleColumns.length === 0) return null;

  const toggle = <T,>(list: T[], item: T) =>
    list.includes(item) ? list.filter((x) => x !== item) : [...list, item];
  const count = refs.length + columnIds.length;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="default" className="h-9 flex-1 justify-start font-normal">
          {count === 0
            ? t('trackersBoard.automationRuleFields.peopleOnTheRow')
            : t('trackersBoard.automationRuleFields.peopleOnTheRowCount', { count })}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="flex w-64 flex-col gap-0.5 p-2" align="start">
        {refOptions.map((option) => (
          <label
            key={option.ref}
            className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-accent"
          >
            <Checkbox
              checked={refs.includes(option.ref)}
              onCheckedChange={() =>
                onChange({ recipientRefs: toggle(refs, option.ref), recipientColumnIds: columnIds })
              }
            />
            {option.label}
          </label>
        ))}
        {peopleColumns.map((column) => (
          <label
            key={column.id}
            className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-accent"
          >
            <Checkbox
              checked={columnIds.includes(column.id)}
              onCheckedChange={() =>
                onChange({ recipientRefs: refs, recipientColumnIds: toggle(columnIds, column.id) })
              }
            />
            {t('trackersBoard.automationRuleFields.peopleInColumn', { column: column.label })}
          </label>
        ))}
      </PopoverContent>
    </Popover>
  );
}
